/**
 * Webhook payload shapes.
 *
 * Two webhook surfaces exist in Phase 1:
 *
 *   POST /api/v1/webhooks/stripe                    → Stripe event envelope
 *   POST /internal/runs/:id/callback                → Executor → orchestrator callback
 *
 * The Stripe event body is opaque from our schema's perspective — Stripe
 * verifies the signature and we hand the raw payload to the Stripe SDK. We
 * still expose a minimal shape so OpenAPI documents the endpoint.
 *
 * The executor callback shape comes directly from IMPLEMENTATION.md §7.2.
 */
import { z } from "zod";
import { ErrorPayloadSchema } from "./errors.js";
import { RunStatusEnumSchema, RunUsageSchema } from "./runs.js";
/** Stripe event envelope — minimal projection; the SDK handles parsing. */
export const StripeWebhookEventSchema = z.object({
    id: z.string(),
    type: z.string(),
    created: z.number().int(),
    data: z.object({
        object: z.record(z.unknown()),
    }),
});
/**
 * Executor → orchestrator callback body. Authenticated via per-run HMAC
 * (RLY-020). Status transitions are validated server-side by the orchestrator
 * before any DB write.
 */
export const ExecutorCallbackPayloadSchema = z.object({
    run_id: z.string().uuid(),
    status: RunStatusEnumSchema,
    output: z.unknown().optional(),
    error: ErrorPayloadSchema.optional(),
    usage: RunUsageSchema.optional(),
});
/** POST /webhooks/lead-form — public lead capture form submission. */
export const LeadFormWebhookBodySchema = z
    .object({
    phone: z
        .string()
        .regex(/^\+[1-9]\d{1,14}$/, "Phone must be E.164 format")
        .optional(),
    email: z.string().email().optional(),
    first_name: z.string().max(100).optional(),
    last_name: z.string().max(100).optional(),
    company: z.string().max(200).optional(),
    message: z.string().max(2000).optional(),
    utm_source: z.string().max(100).optional(),
    utm_medium: z.string().max(100).optional(),
    utm_campaign: z.string().max(200).optional(),
    utm_content: z.string().max(200).optional(),
    metadata: z.record(z.unknown()).default({}),
})
    .refine((v) => v.phone !== undefined || v.email !== undefined, {
    message: "At least one of phone or email is required",
});
//# sourceMappingURL=webhooks.js.map